/**
 * Enhanced Question Component
 * Handles all question types including repeatable fields (multiple W-2s, 1099s, etc.)
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Question as QuestionType, FormData, QuestionnaireResponse } from '../types/questionnaire';
import { CheckCircle, AlertCircle, HelpCircle, Plus, Trash2, ArrowLeft, ArrowRight } from 'lucide-react';
import {
  Input,
  NumberInput,
  Select,
  Textarea,
  DatePicker,
  RadioGroup,
  Checkbox,
  CheckboxGroup,
  Button,
} from './ui';

interface EnhancedQuestionProps {
  question: QuestionType;
  value: any;
  onChange: (value: any) => void;
  onNext: () => void;
  onPrevious: () => void;
  isFirst: boolean;
  isLast: boolean;
  showValidation: boolean;
  formData?: FormData;
  allResponses?: Map<string, QuestionnaireResponse>;
}

const EnhancedQuestion: React.FC<EnhancedQuestionProps> = ({
  question,
  value,
  onChange,
  onNext,
  onPrevious,
  isFirst,
  isLast,
  showValidation,
  formData = {},
  allResponses = new Map()
}) => {
  // Helper to get previous answer from either formData or responses
  const getPreviousAnswer = useCallback((questionId: string): any => {
    // First try formData
    if (formData[questionId] !== undefined) {
      return formData[questionId];
    }
    // Then try responses map
    const response = allResponses.get(questionId);
    return response?.answer ?? null;
  }, [formData, allResponses]);
  const [errors, setErrors] = useState<string[]>([]);
  const [touched, setTouched] = useState(false);

  // Calculate value for calculated fields (moved outside renderInput to use hooks)
  const calculatedValue = useMemo(() => {
    if (question.type !== 'calculated' || !question.calculationLogic?.formula) return 0;
    
    const formula = question.calculationLogic.formula;
    
    // Helper function to get numeric value for a question ID
    const getQuestionValue = (qId: string): number => {
      const answer = getPreviousAnswer(qId);
      if (Array.isArray(answer)) {
        // If answer is an array (e.g., multiple W-2 forms), sum them
        return answer.reduce((sum: number, item: any) => {
          const numValue = typeof item === 'string' ? parseFloat(item) || 0 : (typeof item === 'number' ? item : 0);
          return sum + numValue;
        }, 0);
      }
      const numValue = typeof answer === 'string' ? parseFloat(answer) || 0 : (typeof answer === 'number' ? answer : 0);
      return numValue;
    };
    
    // Helper function to get date value and extract year
    const getDateYear = (qId: string): number => {
      const answer = getPreviousAnswer(qId);
      if (!answer) return 0;
      // Handle date strings (YYYY-MM-DD format)
      if (typeof answer === 'string') {
        const dateMatch = answer.match(/^(\d{4})-/);
        if (dateMatch) {
          return parseInt(dateMatch[1], 10);
        }
        // Try parsing as Date
        const date = new Date(answer);
        if (!isNaN(date.getTime())) {
          return date.getFullYear();
        }
      }
      return 0;
    };
    
    // Handle special date functions: YEAR(NOW()) and YEAR(Q3)
    let processedFormula: string = formula;
    
    // Replace YEAR(NOW()) with current year
    processedFormula = processedFormula.replace(/YEAR\(NOW\(\)\)/g, String(new Date().getFullYear()));
    
    // Handle YEAR(Q3) -> extract year from date answer
    const yearMatches = processedFormula.match(/YEAR\(Q\d+[A-Z]?(_\d+)?\)/g) || [];
    yearMatches.forEach((yearMatch: string) => {
      const qIdMatch = yearMatch.match(/YEAR\((Q\d+[A-Z]?(_\d+)?)\)/);
      if (qIdMatch) {
        const qId = qIdMatch[1];
        const year = getDateYear(qId);
        processedFormula = processedFormula.replace(yearMatch, String(year));
      }
    });
    
    // Extract question IDs from formula (e.g., "Q61 + Q61A - Q61F" -> ["Q61", "Q61A", "Q61F"])
    const questionIds = processedFormula.match(/Q\d+[A-Z]?(_\d+)?/g) || [];
    
    // Get values for each question ID
    const values: { [key: string]: number } = {};
    questionIds.forEach(qId => {
      values[qId] = getQuestionValue(qId);
    });
    
    // Handle SUM() function: SUM(Q1, Q2, Q3) -> sum of values
    const sumMatches = processedFormula.match(/SUM\(([^)]+)\)/g) || [];
    sumMatches.forEach((sumMatch: string) => {
      const innerMatch = sumMatch.match(/SUM\(([^)]+)\)/);
      if (innerMatch) {
        const args = innerMatch[1].split(',').map(arg => arg.trim());
        const sum = args.reduce((total, arg) => {
          // Check if it's a question ID
          if (arg.match(/Q\d+[A-Z]?(_\d+)?/)) {
            return total + getQuestionValue(arg);
          }
          // Otherwise treat as number
          return total + (parseFloat(arg) || 0);
        }, 0);
        processedFormula = processedFormula.replace(sumMatch, String(sum));
      }
    });
    
    // Handle MAX() function: MAX(Q1, Q2) -> maximum of values
    const maxMatches = processedFormula.match(/MAX\(([^)]+)\)/g) || [];
    maxMatches.forEach((maxMatch: string) => {
      const innerMatch = maxMatch.match(/MAX\(([^)]+)\)/);
      if (innerMatch) {
        const args = innerMatch[1].split(',').map(arg => arg.trim());
        const max = Math.max(...args.map(arg => {
          // Check if it's a question ID
          if (arg.match(/Q\d+[A-Z]?(_\d+)?/)) {
            return getQuestionValue(arg);
          }
          // Otherwise treat as number
          return parseFloat(arg) || 0;
        }));
        processedFormula = processedFormula.replace(maxMatch, String(max));
      }
    });
    
    // Replace remaining question IDs with their values in the formula
    questionIds.forEach((qId: string) => {
      processedFormula = processedFormula.replace(new RegExp(qId.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g'), String(values[qId] || 0));
    });
    
    // Evaluate the formula safely
    try {
      // Remove any non-math characters for safety, but keep +, -, *, /, (, ), ., numbers, and function names
      const safeFormula = processedFormula.replace(/[^0-9+\-*/().\s]/g, '');
      // Use Function constructor for safe evaluation (only math operations)
      // eslint-disable-next-line no-new-func
      const result = Function(`"use strict"; return (${safeFormula})`)();
      return typeof result === 'number' && !isNaN(result) ? result : 0;
    } catch (e) {
      console.error('Error calculating formula:', e, 'Formula:', processedFormula);
      return 0;
    }
  }, [question.type, question.calculationLogic?.formula, getPreviousAnswer]);

  // Auto-update the value when calculation changes (only if different)
  useEffect(() => {
    if (question.type === 'calculated' && Math.abs(calculatedValue - (value || 0)) > 0.01) {
      onChange(calculatedValue);
    }
  }, [calculatedValue, question.type, value, onChange]);

  const validateAnswer = useCallback(() => {
    const validationErrors: string[] = [];
    
    // Check if required question has a value
    if (question.required) {
      if (value === null || value === undefined || value === '' || 
          (Array.isArray(value) && value.length === 0)) {
        validationErrors.push('This field is required');
      }
    }
    
    // Check validation rules (only if value exists)
    if (question.validation && value !== null && value !== undefined && value !== '') {
      // Handle string validation (rule name with inline pattern)
      if (typeof question.validation === 'string') {
        // Use inline pattern if provided
        if (question.pattern && typeof value === 'string') {
          try {
            const regex = new RegExp(question.pattern);
            if (!regex.test(value)) {
              validationErrors.push(question.error_message || `Invalid format for ${question.validation}`);
            }
          } catch (e) {
            console.error('Invalid regex pattern:', question.pattern);
          }
        }
        
        // Check min_length
        if (question.min_length && typeof value === 'string' && value.length < question.min_length) {
          validationErrors.push(question.error_message || `Minimum ${question.min_length} characters required`);
        }
        
        // Check max_length
        if (question.max_length && typeof value === 'string' && value.length > question.max_length) {
          validationErrors.push(question.error_message || `Maximum ${question.max_length} characters allowed`);
        }
      } else if (Array.isArray(question.validation)) {
        // Handle array of validation rules
        question.validation.forEach(rule => {
          switch (rule.type) {
            case 'required':
              // Already handled above
              break;
            case 'min':
              if (typeof value === 'number' && value < (rule.value as number)) {
                validationErrors.push(rule.message);
              }
              break;
            case 'max':
              if (typeof value === 'number' && value > (rule.value as number)) {
                validationErrors.push(rule.message);
              }
              break;
            case 'pattern':
              if (typeof value === 'string' && rule.value) {
                try {
                  const regex = rule.value instanceof RegExp ? rule.value : new RegExp(rule.value);
                  if (!regex.test(value)) {
                    validationErrors.push(rule.message);
                  }
                } catch (e) {
                  console.error('Invalid regex pattern:', rule.value);
                }
              }
              break;
            case 'custom':
              if (rule.validator && !rule.validator(value)) {
                validationErrors.push(rule.message);
              }
              break;
          }
        });
      }
    }
    
    setErrors(validationErrors);
    return validationErrors.length === 0;
  }, [question.required, question.validation, question.pattern, question.error_message, question.min_length, question.max_length, value]);

  useEffect(() => {
    if (showValidation && touched) {
      validateAnswer();
    }
  }, [value, showValidation, touched, validateAnswer]);

  const handleChange = (newValue: any) => {
    setTouched(true);
    onChange(newValue);
  };

  const handleNext = () => {
    setTouched(true);
    // Always validate when clicking next - this will set errors if validation fails
    const isValid = validateAnswer();
    if (isValid) {
      // Call parent's onNext handler only if valid
      onNext();
    }
    // If not valid, errors are already set by validateAnswer() and will be displayed by UI components
  };

  // Handle repeatable fields (e.g., multiple W-2 forms)
  const handleAddRepeatableItem = () => {
    const currentValues = Array.isArray(value) ? value : [];
    const newItem = createDefaultRepeatableItem();
    handleChange([...currentValues, newItem]);
  };

  const handleRemoveRepeatableItem = (index: number) => {
    const currentValues = Array.isArray(value) ? value : [];
    handleChange(currentValues.filter((_, i) => i !== index));
  };

  const handleUpdateRepeatableItem = (index: number, field: string, fieldValue: any) => {
    const currentValues = Array.isArray(value) ? value : [];
    const updated = [...currentValues];
    updated[index] = { ...updated[index], [field]: fieldValue };
    handleChange(updated);
  };

  const createDefaultRepeatableItem = () => {
    // Create default structure based on question ID
    if (question.id.includes('W2') || question.id.includes('w2')) {
      return {
        employerName: '',
        wages: 0,
        federalWithheld: 0,
        socialSecurityWages: 0,
        socialSecurityWithheld: 0,
        medicareWages: 0,
        medicareWithheld: 0,
        stateWages: 0,
        stateWithheld: 0,
        localWages: 0
      };
    }
    // Add more default structures for other repeatable types
    return {};
  };

  const renderInput = () => {
    // Handle repeatable fields
    if (question.repeatable && Array.isArray(value)) {
      return (
        <div className="space-y-4">
          {value.map((item: any, index: number) => (
            <div key={index} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
              <div className="flex justify-between items-center mb-4">
                <h4 className="font-medium text-gray-900">
                  {question.id.includes('W2') ? `W-2 Form #${index + 1}` : `Entry #${index + 1}`}
                </h4>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleRemoveRepeatableItem(index)}
                  className="text-red-600 hover:text-red-700"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
              {renderRepeatableFields(item, index)}
            </div>
          ))}
          <Button
            variant="outline"
            onClick={handleAddRepeatableItem}
            leftIcon={<Plus className="w-4 h-4" />}
          >
            Add Another
          </Button>
        </div>
      );
    }

    // Regular input fields
    switch (question.type) {
      case 'radio':
        const radioOptions = Array.isArray(question.options) ? question.options : [];
        return (
          <RadioGroup
                  name={question.id}
            value={value || ''}
            onChange={handleChange}
            options={radioOptions}
            error={errors.length > 0 ? errors[0] : undefined}
            orientation="vertical"
          />
        );

      case 'checkbox':
      case 'multiselect':
        // Check if this is a dynamic dependent checkbox question
        // Determined by: options="dynamic_dependent_checkboxes" in decision tree JSON
        // Also check if question text mentions "dependents" and "disabilities"
        // No hardcoded question IDs - all logic driven by decision tree JSON
        const isDisabilityQuestion = question.question?.toLowerCase().includes('dependent') && 
                                     question.question?.toLowerCase().includes('disabilit');
        const hasNoOptions = !question.options || question.options.length === 0;
        const isDynamicDependentCheckbox = isDisabilityQuestion && hasNoOptions;
        
        console.log('Checkbox/Multiselect - Question ID:', question.id, 'Type:', question.type, 'Has options:', !hasNoOptions, 'Is disability question:', isDisabilityQuestion, 'Is dynamic:', isDynamicDependentCheckbox);
        
        if (isDynamicDependentCheckbox) {
          // Dynamic checkboxes based on number of dependents
          // Get dependsOn from conditional.dependsOn in decision tree JSON (no hardcoded values)
          const dependsOnQuestionId = question.dependsOn || question.conditional?.dependsOn || null;
          let dependentCount = 0;
          
          if (dependsOnQuestionId) {
            const previousAnswer = getPreviousAnswer(dependsOnQuestionId);
            if (previousAnswer !== undefined && previousAnswer !== null && previousAnswer !== '') {
              dependentCount = parseInt(String(previousAnswer)) || 0;
            }
          }
          
          console.log('Dynamic checkbox - Question ID:', question.id, 'Dependent count:', dependentCount, 'FormData keys:', Object.keys(formData), 'Responses keys:', Array.from(allResponses.keys()));
          
          // Get selected dependents (array of indices or names)
          const selectedDependents = Array.isArray(value) ? value : (value ? [value] : []);
          
          if (dependentCount === 0) {
            return (
              <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-md">
                <p className="text-sm text-yellow-800">
                  Please answer the previous question about the number of dependents first.
                </p>
              </div>
            );
          }
          
          return (
            <div className="space-y-3">
              <p className="text-sm text-gray-600 mb-3">
                Select which of your {dependentCount} dependent{dependentCount !== 1 ? 's' : ''} have disabilities:
              </p>
              {Array.from({ length: dependentCount }, (_, index) => {
                const dependentLabel = `Dependent ${index + 1}`;
                const isChecked = selectedDependents.includes(dependentLabel) || 
                                 selectedDependents.includes(index) ||
                                 selectedDependents.includes(String(index + 1));
                
                return (
                  <div key={index} className="p-3 rounded-md hover:bg-gray-50 transition-colors">
                    <Checkbox
                      id={`${question.id}-dependent-${index}`}
                      label={dependentLabel}
                      checked={isChecked}
                      onChange={(e) => {
                        const currentValues = Array.isArray(value) ? [...value] : (value ? [value] : []);
                        if (e.target.checked) {
                          // Add dependent label
                          if (!currentValues.includes(dependentLabel)) {
                            currentValues.push(dependentLabel);
                          }
                        } else {
                          // Remove dependent label
                          handleChange(currentValues.filter(v => 
                            v !== dependentLabel && v !== index && v !== String(index + 1)
                          ));
                          return;
                        }
                        handleChange(currentValues);
                      }}
                    />
                  </div>
                );
              })}
              {selectedDependents.length > 0 && (
                <p className="text-xs text-gray-500 mt-2">
                  Selected: {selectedDependents.join(', ')}
                </p>
              )}
              {showValidation && errors.length > 0 && (
                <div className="mt-2">
                  {errors.map((error, index) => (
                    <p key={index} className="text-sm text-red-600">
                      {error}
                    </p>
                  ))}
                </div>
              )}
            </div>
          );
        }
        
        // Regular checkbox/multiselect with static options
        const checkboxOptions = Array.isArray(question.options) ? question.options : [];
        return (
          <CheckboxGroup
            name={question.id}
            value={Array.isArray(value) ? value : []}
            onChange={(values) => handleChange(values)}
            options={checkboxOptions}
            error={errors.length > 0 ? errors[0] : undefined}
            orientation="vertical"
          />
        );

      case 'select':
        const selectOptions = Array.isArray(question.options) ? question.options : [];
        return (
          <Select
            id={question.id}
            value={value || ''}
            onChange={(e) => handleChange(e.target.value)}
            options={selectOptions}
            placeholder="Select an option"
            error={errors.length > 0 ? errors[0] : undefined}
            helperText={question.helpText}
            required={question.required}
            autoComplete={question.autocomplete || 'on'}
          />
        );

      case 'text':
        // Helper function to format value with mask (e.g., SSN: ###-##-####)
        const formatWithMask = (inputValue: string, mask?: string): string => {
          if (!mask || !inputValue) return inputValue;
          
          // Remove non-alphanumeric characters from input for processing
          const cleanValue = inputValue.replace(/[^a-zA-Z0-9]/g, '');
          let result = '';
          let valueIndex = 0;
          
          for (let i = 0; i < mask.length && valueIndex < cleanValue.length; i++) {
            if (mask[i] === '#') {
              result += cleanValue[valueIndex];
              valueIndex++;
            } else {
              result += mask[i];
              // If current input char matches the mask separator, skip it
              if (cleanValue[valueIndex] === mask[i]) {
                valueIndex++;
              }
            }
          }
          return result;
        };
        
        // Handle masked input change
        const handleMaskedChange = (e: React.ChangeEvent<HTMLInputElement>) => {
          const inputValue = e.target.value;
          if (question.mask) {
            // Format with mask as user types
            const formatted = formatWithMask(inputValue, question.mask);
            handleChange(formatted);
          } else {
            handleChange(inputValue);
          }
        };
        
        // Determine max length from mask or explicit max_length
        const textMaxLength = question.mask 
          ? question.mask.length 
          : question.max_length;
        
        return (
          <Input
            id={question.id}
            type={question.sensitive ? 'password' : 'text'}
            value={value || ''}
            onChange={handleMaskedChange}
            placeholder={question.placeholder || (question.mask ? question.mask.replace(/#/g, 'X') : undefined)}
            error={errors.length > 0 ? errors[0] : undefined}
            helperText={question.helpText}
            required={question.required}
            maxLength={textMaxLength}
            pattern={question.pattern}
            autoComplete={question.sensitive ? 'off' : question.autocomplete || 'on'}
          />
        );

      case 'number':
        // For currency fields, use step 0.01 for decimal precision
        // If step is explicitly set, use it; otherwise default to 1 for integers, 0.01 for currency-like fields
        const defaultStep = question.step !== undefined 
          ? question.step 
          : (question.placeholder?.includes('€') || question.placeholder?.includes('EUR') || question.helpText?.toLowerCase().includes('currency'))
            ? 0.01 
            : 1;
        
        // Determine if this is an integer field (no decimals) or decimal field
        const isIntegerField = defaultStep === 1 || question.step === 1;
        const stepValue = isIntegerField ? 1 : defaultStep;
        
        return (
          <NumberInput
            id={question.id}
            value={value}
            onChange={(numValue) => handleChange(numValue)}
            allowDecimals={!isIntegerField}
            allowNegative={false}
            min={question.min}
            max={question.max}
            step={stepValue}
            placeholder={question.placeholder}
            error={errors.length > 0 ? errors[0] : undefined}
            helperText={question.helpText}
            required={question.required}
          />
        );

      case 'date':
        return (
          <DatePicker
            id={question.id}
            value={value || ''}
            onChange={(e) => handleChange(e.target.value)}
            error={errors.length > 0 ? errors[0] : undefined}
            helperText={question.helpText}
            required={question.required}
          />
        );

      case 'textarea':
        return (
          <Textarea
            id={question.id}
            value={value || ''}
            onChange={(e) => handleChange(e.target.value)}
            placeholder={question.placeholder}
            rows={question.id.includes('address') ? 3 : 4}
            error={errors.length > 0 ? errors[0] : undefined}
            helperText={question.helpText}
            required={question.required}
          />
        );

      case 'calculated':
        // Use the calculated value from component-level useMemo
        // Determine if this is a currency/monetary field based on question context
        const isCurrencyField = 
          question.question.toLowerCase().includes('total') ||
          question.question.toLowerCase().includes('amount') ||
          question.question.toLowerCase().includes('income') ||
          question.question.toLowerCase().includes('tax') ||
          question.question.toLowerCase().includes('deduction') ||
          question.question.toLowerCase().includes('worth') ||
          question.question.toLowerCase().includes('asset') ||
          question.question.toLowerCase().includes('liabilit') ||
          question.question.toLowerCase().includes('€') ||
          question.question.toLowerCase().includes('eur') ||
          question.calculationLogic?.display?.includes('€') ||
          question.calculationLogic?.display?.includes('EUR');
        
        // Format based on field type
        const formattedValue = isCurrencyField 
          ? calculatedValue.toLocaleString('en-US', { 
              minimumFractionDigits: 2, 
              maximumFractionDigits: 2 
            })
          : Number.isInteger(calculatedValue) 
            ? calculatedValue.toString()
            : calculatedValue.toLocaleString('en-US', { maximumFractionDigits: 2 });
        
        // Only show currency symbol for monetary fields
        const displayValue = isCurrencyField ? `€${formattedValue}` : formattedValue;
        
        return (
          <div className="space-y-2">
            <Input
              id={question.id}
              type="text"
              value={displayValue}
              readOnly={true}
              className="bg-gray-50 font-semibold text-lg"
              error={errors.length > 0 ? errors[0] : undefined}
              helperText={question.helpText || question.calculationLogic?.description}
            />
            {question.calculationLogic?.display && (
              <p className="text-sm text-gray-600 italic">
                {question.calculationLogic.display}
              </p>
            )}
          </div>
        );

      case 'multi-input':
        // Dynamic input fields based on a previous answer (e.g., number of dependents)
        // Get the count from formData based on dependsOn question ID
        // dependsOn comes from conditional.dependsOn in the decision tree JSON, no hardcoded values
        const dependsOnQuestionId = question.dependsOn || question.conditional?.dependsOn || null;
        
        // Try to get the count from formData or responses (which contains all previous answers)
        let count = 0;
        if (dependsOnQuestionId) {
          const previousAnswer = getPreviousAnswer(dependsOnQuestionId);
          if (previousAnswer !== undefined && previousAnswer !== null && previousAnswer !== '') {
            count = parseInt(String(previousAnswer)) || 0;
          }
        }
        
        // Ensure value is an array
        const agesArray = Array.isArray(value) ? value : (value ? [value] : []);
        
        // Ensure array has the right length
        while (agesArray.length < count) {
          agesArray.push('');
        }
        while (agesArray.length > count && count > 0) {
          agesArray.pop();
        }
        
        const inputType = question.inputType || 'number';
        
        if (count === 0) {
          // Dynamic error message based on dependsOn question ID from decision tree
          const dependsOnQuestionText = dependsOnQuestionId 
            ? `the previous question (${dependsOnQuestionId})` 
            : 'the previous question';
          return (
            <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-md">
              <p className="text-sm text-yellow-800">
                Please answer {dependsOnQuestionText} first.
              </p>
            </div>
          );
        }
        
        return (
          <div className="space-y-3">
            <p className="text-sm text-gray-600 mb-3">
              Enter the age for each of your {count} dependent{count !== 1 ? 's' : ''}:
            </p>
            {Array.from({ length: count }, (_, index) => (
              <div key={index} className="flex items-center space-x-3">
                <label className="text-sm font-medium text-gray-700 w-32">
                  Dependent {index + 1}:
                </label>
                {inputType === 'number' ? (
                  <NumberInput
                    id={`${question.id}-${index}`}
                    value={agesArray[index] || null}
                    onChange={(numValue) => {
                    const newAges = [...agesArray];
                      newAges[index] = numValue || '';
                    handleChange(newAges);
                  }}
                    allowDecimals={false}
                    allowNegative={false}
                  min={question.min}
                  max={question.max}
                  step={question.step || 1}
                    placeholder="Age"
                    className="flex-1"
                  />
                ) : (
                  <Input
                    id={`${question.id}-${index}`}
                    type="text"
                    value={agesArray[index] || ''}
                    onChange={(e) => {
                      const newAges = [...agesArray];
                      newAges[index] = e.target.value;
                      handleChange(newAges);
                    }}
                    placeholder="Enter value"
                    className="flex-1"
                />
                )}
                <span className="text-sm text-gray-500">years old</span>
              </div>
            ))}
            <p className="text-xs text-gray-500 mt-2">
              Ages will be saved as: {agesArray.filter(a => a !== '').join(', ') || 'None entered'}
            </p>
          </div>
        );

      default:
        return null;
    }
  };

  const renderRepeatableFields = (item: any, index: number) => {
    // Render fields based on question type
    if (question.id.includes('W2') || question.id.includes('w2')) {
      return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Input
              id={`${question.id}-employer-${index}`}
              label="Employer Name"
              type="text"
              value={item.employerName || ''}
              onChange={(e) => handleUpdateRepeatableItem(index, 'employerName', e.target.value)}
            />
          </div>
          <div>
            <NumberInput
              id={`${question.id}-wages-${index}`}
              label="Wages"
              value={item.wages || 0}
              onChange={(numValue) => handleUpdateRepeatableItem(index, 'wages', numValue || 0)}
              allowDecimals={true}
              allowNegative={false}
              step={0.01}
            />
          </div>
          {/* Add more W-2 fields as needed */}
        </div>
      );
    }
    return null;
  };

  const isRequired = question.required;
  const hasErrors = errors.length > 0;
  // For required questions, value must be provided. For optional questions, any value (including empty) is valid
  const hasValue = value !== null && value !== undefined && value !== '' && 
                   !(Array.isArray(value) && value.length === 0);
  const isValid = !hasErrors && (!isRequired || hasValue);

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 animate-fade-in">
      {/* Question Header */}
      <div className="mb-6">
        <div className="flex items-start space-x-3">
          <div className="flex-shrink-0">
            {isValid ? (
              <CheckCircle className="w-6 h-6 text-green-500" />
            ) : hasErrors ? (
              <AlertCircle className="w-6 h-6 text-red-500" />
            ) : (
              <div className="w-6 h-6 rounded-full border-2 border-gray-300" />
            )}
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {question.question}
              {isRequired && <span className="text-red-500 ml-1">*</span>}
            </h3>
            {question.helpText && (
              <div className="flex items-start space-x-2 text-sm text-gray-600">
                <HelpCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <p>{question.helpText}</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Input Field */}
      <div className="mb-6">
        {renderInput()}
      </div>

      {/* Navigation Buttons */}
      <div className="flex justify-between items-center pt-4 border-t border-gray-200">
        <Button
          onClick={onPrevious}
          disabled={isFirst}
          variant={isFirst ? 'ghost' : 'outline'}
          leftIcon={<ArrowLeft className="w-4 h-4" />}
        >
          Previous
        </Button>
        
        <div className="flex space-x-3">
          {!isLast && (
            <Button
              onClick={handleNext}
              variant="primary"
              disabled={!isValid}
              rightIcon={<ArrowRight className="w-4 h-4" />}
            >
              Next
            </Button>
          )}
          
          {isLast && (
            <Button
              onClick={handleNext}
              variant="primary"
              disabled={!isValid}
              className={isValid ? 'bg-green-600 hover:bg-green-700 focus:ring-green-500' : 'bg-green-400 hover:bg-green-500 focus:ring-green-300'}
              rightIcon={<CheckCircle className="w-4 h-4" />}
            >
              Complete
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default EnhancedQuestion;
